import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import { useEventDetails } from '../../../hooks/useEventDetails';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import FormInput from '../../../components/ui/Form/FormInput';
import FormTextarea from '../../../components/ui/Form/FormTextarea';
import formStyles from '../../../components/ui/Form/Form.module.css';
import { Ticket } from '../../../types/ticket.types';
import { OrganizerPanelOutletContext, TicketFormData } from '../../../types/organizerPanel.types';
import { extractEventIdFromAdminUrl } from '../../../utils/urlSlug';
import { timestampToApiFormat } from '../../../utils/dateTimeFormatters';
import styles from './EventTicketsPage.module.css';

const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

const EventTicketsPage: React.FC = () => {
  const { channel, event: basicEvent } = useOutletContext<OrganizerPanelOutletContext>();
  const location = useLocation();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  // Extract eventID from URL as fallback when basicEvent isn't available yet
  const eventID = useMemo(() => {
    if (basicEvent?.eventID) return basicEvent.eventID;
    return extractEventIdFromAdminUrl(location.pathname) || '';
  }, [basicEvent?.eventID, location.pathname]);

  // Fetch full event details
  const { event: eventDetails, loading: eventLoading, refreshEvent } = useEventDetails({
    token: token || null,
    eventID,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Ref to store initial form data for unsaved changes detection
  const initialFormDataRef = useRef<TicketFormData | null>(null);

  // Form state
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    type: 'normal',
    description: '',
    price: '0',
    currency: 'TRY',
    trackInventory: false,
    inventory: '0',
  });

  // Recalculate isFree and update event only if the value changed.
  // Fetches fresh event details to build the full payload, changing only isFree.
  // 1 = free, 2 = paid, 3 = mixed
  const updateEventTypeIfNeeded = async (customTickets: { price: number }[]) => {
    if (!token || !eventDetails?.eventID || !channel?.channelID) return;

    let newIsFree = 1;
    if (customTickets.length > 0) {
      const hasFree = customTickets.some(t => t.price === 0);
      const hasPaid = customTickets.some(t => t.price > 0);
      if (hasFree && hasPaid) newIsFree = 3;
      else if (hasPaid) newIsFree = 2;
    }

    const currentIsFree = Number(eventDetails.isFree) || 1;
    if (newIsFree === currentIsFree) return;

    try {
      const res = await Services.SocialHiveGetEventDetails(token, eventDetails.eventID);
      const evt = res.data?.result;
      if (!evt) return;

      await Services.SocialHiveUpdateEvent(token, {
        eventID: evt.eventID,
        channelID: evt.channelID,
        title: evt.title,
        description: evt.description,
        isFree: newIsFree,
        eventStartDateTime: timestampToApiFormat(evt.eventStartDateTime),
        eventEndDateTime: timestampToApiFormat(evt.eventEndDateTime),
        isOnline: evt.isOnline === "1",
        website: evt.website || "",
        locationAddress: evt.locationAddress || "",
        locationLatitude: evt.locationLatitude || "",
        locationLongitude: evt.locationLongitude || "",
        locationAddressDefinition: evt.locationAddressDefinition || "",
        city: evt.city || null,
        organizedBy: evt.organizedBy || "",
        hasTickets: evt.hasTickets === "1",
        ticketSaleStartDateTime: evt.ticketSaleStartDateTime ? timestampToApiFormat(evt.ticketSaleStartDateTime) : null,
        contactEmail: evt.contactEmail || "",
        ticketSaleNotificationEmail: evt.ticketSaleNotificationEmail || "",
        customerNotificationMailTemplate: evt.customerNotificationMailTemplate || "",
        contactPhone: evt.contactPhone || "",
        socialMediaLinks: evt.socialMediaLinks || {},
        usersCanSendMessage: evt.usersCanSendMessage ?? true,
        isPublic: Number(evt.isPublic || "1"),
        status: 1,
      });
    } catch {
      // Silent fail — the ticket operation itself succeeded
    }
  };

  // Filter out sponsor tickets (shown in Sponsorships page)
  const normalTickets = (eventDetails?.tickets || []).filter(
    (ticket) => {
      const ticketType = (ticket.type || '').trim().toLowerCase();
      return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
    }
  );
  const isRsvpEvent = useMemo(() => {
    const allTickets = eventDetails?.tickets || [];
    const hasJoinTicket = allTickets.some((ticket) => isLcvJoinTicketType(ticket.type));
    return hasJoinTicket && normalTickets.length === 0;
  }, [eventDetails?.tickets, normalTickets.length]);

  // Reset form when opening add modal
  useEffect(() => {
    if (showAddModal && !selectedTicket) {
      const initialFormData: TicketFormData = {
        title: '',
        type: '', // Empty by default, user can enter custom type
        description: '',
        price: '0',
        currency: 'TRY', // Fixed to TRY
        trackInventory: false,
        inventory: '0',
      };
      setFormData(initialFormData);
      initialFormDataRef.current = initialFormData;
    }
  }, [showAddModal, selectedTicket]);

  // Load ticket data when opening edit modal
  useEffect(() => {
    if (showEditModal && selectedTicket) {
      // Ensure price is a number, not a string with currency
      const ticketPrice = typeof selectedTicket.price === 'number' 
        ? selectedTicket.price 
        : parseFloat(String(selectedTicket.price || 0).replace(/[^\d.]/g, '')) || 0;
      const initialFormData: TicketFormData = {
        title: selectedTicket.title || '',
        type: selectedTicket.type || '', // Use actual ticket type
        description: selectedTicket.description || '',
        price: String(ticketPrice),
        currency: 'TRY', // Fixed to TRY
        trackInventory: selectedTicket.trackInventory === true || selectedTicket.trackInventory === "1",
        inventory: String(selectedTicket.inventory || 0),
      };
      setFormData(initialFormData);
      initialFormDataRef.current = initialFormData;
    }
  }, [showEditModal, selectedTicket]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!showAddModal && !showEditModal) {
      return false;
    }
    if (!initialFormDataRef.current) {
      // For add modal, check if any field has been filled
      if (showAddModal) {
        return formData.title.trim() !== '' ||
               formData.type.trim() !== '' ||
               formData.description.trim() !== '' ||
               formData.price !== '0' ||
               formData.trackInventory ||
               formData.inventory !== '0';
      }
      return false;
    }
    
    const initial = initialFormDataRef.current;
    
    // Compare form fields
    return (
      formData.title.trim() !== initial.title.trim() ||
      formData.type.trim() !== initial.type.trim() ||
      formData.description.trim() !== initial.description.trim() ||
      formData.price !== initial.price ||
      formData.currency !== initial.currency ||
      formData.trackInventory !== initial.trackInventory ||
      formData.inventory !== initial.inventory
    );
  }, [showAddModal, showEditModal, formData]);

  const handleAddTicket = () => {
    if (isRsvpEvent) {
      toastError(
        getTranslation('organizerPanel.rsvpEventTicketsManaged') ||
        'Bu etkinlik LCV modunda olusturuldugu icin ozel bilet ekleyemezsiniz.'
      );
      return;
    }

    setSelectedTicket(null);
    setShowAddModal(true);
  };

  const handleEditTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleDeleteTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowDeleteModal(true);
  };

  const handleCloseTicketModal = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedTicket(null);
      initialFormDataRef.current = null;
    }
  };

  const handleUnsavedModalClose = () => {
    setShowUnsavedModal(false);
  };

  const handleUnsavedModalDiscard = () => {
    // Reset form to initial values
    if (initialFormDataRef.current) {
      setFormData(initialFormDataRef.current);
    } else {
      // Reset to empty form for add modal
      setFormData({
        title: '',
        type: '',
        description: '',
        price: '0',
        currency: 'TRY',
        trackInventory: false,
        inventory: '0',
      });
    }
    
    setShowUnsavedModal(false);
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedTicket(null);
    initialFormDataRef.current = null;
  };

  const handleUnsavedModalSave = async () => {
    setShowUnsavedModal(false);
    await handleSaveTicket();
  };

  const handleSaveTicket = async () => {
    if (!token || !channel?.channelID || !eventDetails?.eventID) return;
    if (isRsvpEvent) {
      toastError(
        getTranslation('organizerPanel.rsvpEventTicketsManaged') ||
        'Bu etkinlik LCV modunda olusturuldugu icin ozel bilet ekleyemezsiniz.'
      );
      return;
    }

    if (!formData.title.trim()) {
      toastError(getTranslation('organizerPanel.ticketTitleRequired') || 'Bilet başlığı gereklidir');
      return;
    }
    if (!formData.type.trim()) {
      toastError(getTranslation('organizerPanel.ticketTypeRequired') || 'Bilet tipi gereklidir');
      return;
    }
    if (!formData.description.trim()) {
      toastError(getTranslation('organizerPanel.ticketDescriptionRequired') || 'Ticket description is required.');
      return;
    }
    if (formData.type.trim().toLowerCase() === 'sponsor') {
      toastError(getTranslation('organizerPanel.ticketTypeSponsorNotAllowed') || 'Bilet tipi "sponsor" olamaz. Sponsor biletleri Sponsorships bölümünden eklenebilir.');
      return;
    }
    if (isLcvJoinTicketType(formData.type)) {
      toastError(
        getTranslation('organizerPanel.ticketTypeReservedName') ||
        '"togather-join-ticket" rezerv bir bilet tipi adıdır.'
      );
      return;
    }
    if (formData.trackInventory && (!formData.inventory || parseInt(formData.inventory) < 0)) {
      toastError(getTranslation('organizerPanel.inventoryRequired') || 'Envanter bilgisi gereklidir');
      return;
    }

    setSaving(true);
    try {
      // Clean price: remove any non-numeric characters except decimal point
      const cleanedPrice = formData.price.replace(/[^\d.]/g, '');
      const price = parseFloat(cleanedPrice) || 0;
      const inventory = formData.trackInventory ? (parseInt(formData.inventory) || 0) : null;
      const ticketType = formData.type.trim() || 'normal'; // Use custom type or default to 'normal'

      if (selectedTicket) {
        // Update ticket
        await Services.SocialHiveUpdateTicketToEvent(
          token,
          channel.channelID,
          eventDetails.eventID,
          selectedTicket.ticketID,
          formData.title.trim(),
          ticketType,
          formData.description.trim(),
          price,
          formData.currency,
          formData.trackInventory,
          inventory
        );
        toastSuccess(getTranslation('organizerPanel.ticketUpdated') || 'Bilet güncellendi');
      } else {
        // Add ticket
        await Services.SocialHiveAddTicketToEvent(
          token,
          channel.channelID,
          eventDetails.eventID,
          formData.title.trim(),
          ticketType,
          formData.description.trim(),
          price,
          formData.currency,
          formData.trackInventory,
          inventory
        );
        toastSuccess(getTranslation('organizerPanel.ticketAdded') || 'Bilet eklendi');
      }

      // Recalculate isFree based on resulting custom tickets
      const existingCustom = (eventDetails.tickets || [])
        .filter((ticket) => {
          const ticketType = (ticket.type || '').trim().toLowerCase();
          return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
        });

      if (selectedTicket) {
        const updatedCustom = existingCustom.map(t =>
          t.ticketID === selectedTicket.ticketID
            ? { price }
            : { price: Number(t.price) }
        );
        await updateEventTypeIfNeeded(updatedCustom);
      } else {
        const updatedCustom = [
          ...existingCustom.map(t => ({ price: Number(t.price) })),
          { price },
        ];
        await updateEventTypeIfNeeded(updatedCustom);
      }

      // Refresh event to get updated tickets list
      refreshEvent();
      
      // Close modal and reset
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedTicket(null);
      initialFormDataRef.current = null;
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        (selectedTicket 
          ? getTranslation('organizerPanel.ticketUpdateError') || 'Bilet güncellenemedi'
          : getTranslation('organizerPanel.ticketAddError') || 'Bilet eklenemedi');
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!token || !selectedTicket?.ticketID || !eventDetails?.eventID) return;

    try {
      await Services.SocialHiveRemoveTicketFromEvent(
        token,
        eventDetails.eventID,
        selectedTicket.ticketID
      );
      toastSuccess(getTranslation('organizerPanel.ticketDeleted') || 'Bilet silindi');

      // Recalculate isFree without the deleted ticket
      const remainingCustom = (eventDetails.tickets || [])
        .filter((ticket) => {
          const ticketType = (ticket.type || '').trim().toLowerCase();
          return ticketType !== 'sponsor' &&
            !isLcvJoinTicketType(ticketType) &&
            ticket.ticketID !== selectedTicket.ticketID;
        })
        .map(t => ({ price: Number(t.price) }));
      await updateEventTypeIfNeeded(remainingCustom);

      // Refresh event to get updated tickets list
      refreshEvent();
      
      // Close modal
      setShowDeleteModal(false);
      setSelectedTicket(null);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.ticketDeleteError') || 'Bilet silinemedi';
      toastError(errorMessage);
    }
  };


  return (
    <div className={styles.eventTicketsPage}>
      {(eventLoading || !eventDetails) ? (
        <>
          <div className={styles.header}>
            <LoadingSkeleton height={32} width="200px" />
            <LoadingSkeleton height={40} width="120px" />
          </div>
          <div className={styles.content}>
            <div className={styles.ticketsList}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`skeleton-${index}`} className={styles.ticketCard}>
                  <div style={{ marginBottom: '12px' }}>
                    <LoadingSkeleton height={24} width="60%" />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <LoadingSkeleton height={16} width="80%" />
                  </div>
                  <LoadingSkeleton height={16} width="40%" />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
      <Modal
        isOpen={showAddModal || showEditModal}
        onClose={handleCloseTicketModal}
        title={selectedTicket
          ? (getTranslation('organizerPanel.editTicket') || 'Bilet Düzenle')
          : (getTranslation('organizerPanel.addTicket') || 'Bilet Ekle')
        }
        variant="default"
        className={styles.ticketModal}
      >
        <div className={styles.modalContent}>
          <div className={formStyles.formFields}>
                <div className={formStyles.formField}>
                  <label className={styles.label}>
                    {getTranslation('organizerPanel.ticketTitle') || 'Bilet Başlığı'} *
                  </label>
                  <FormInput
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={getTranslation('organizerPanel.ticketTitlePlaceholder') || 'Bilet başlığı'}
                  />
                </div>

                <div className={formStyles.formField}>
                  <label className={styles.label}>
                    {getTranslation('organizerPanel.ticketDescription') || 'Açıklama'} *
                  </label>
                  <FormTextarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder={getTranslation('organizerPanel.ticketDescriptionPlaceholder') || 'Bilet açıklaması'}
                  />
                </div>

                <div className={formStyles.formField}>
                  <label className={styles.label}>
                    {getTranslation('organizerPanel.ticketType') || 'Bilet Tipi'} *
                  </label>
                  <FormInput
                    type="text"
                    value={formData.type}
                    onChange={(e) => {
                      const value = e.target.value;
                      const normalizedValue = value.trim().toLowerCase();
                      // Prevent typing restricted ticket types and show explicit warnings.
                      if (normalizedValue === 'sponsor') {
                        toastError(
                          getTranslation('organizerPanel.ticketTypeSponsorNotAllowed') ||
                          'Bilet tipi "sponsor" olamaz. Sponsor biletleri Sponsorships bölümünden eklenebilir.'
                        );
                        return;
                      }
                      if (isLcvJoinTicketType(value)) {
                        toastError(
                          getTranslation('organizerPanel.ticketTypeReservedName') ||
                          '"togather-join-ticket" rezerv bir bilet tipi adıdır.'
                        );
                        return;
                      }
                      setFormData({ ...formData, type: value });
                    }}
                    placeholder={getTranslation('organizerPanel.ticketTypePlaceholder') || 'Bilet tipi (örn: Normal, VIP, Early Bird)'}
                  />
                  <p className={styles.helpText}>
                    {getTranslation('organizerPanel.ticketTypeHelp') || 'Not: "sponsor" tipi yalnızca Sponsorships bölümünden eklenebilir.'}
                  </p>
                </div>

                <div className={formStyles.formRow}>
                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.price') || 'Fiyat'} *
                    </label>
                    <FormInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => {
                        // Only allow numeric input (including decimal point)
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        setFormData({ ...formData, price: value });
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.currency') || 'Para Birimi'} *
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className={formStyles.select}
                      disabled
                    >
                      <option value="TRY">TRY</option>
                    </select>
                  </div>
                </div>

                <div className={formStyles.formField}>
                  <label className={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={formData.trackInventory}
                      onChange={(e) => setFormData({ ...formData, trackInventory: e.target.checked, inventory: e.target.checked ? formData.inventory : '0' })}
                      className={styles.checkbox}
                    />
                    <span>{getTranslation('organizerPanel.trackInventory') || 'Envanter Takibi'}</span>
                  </label>
                </div>

                {formData.trackInventory && (
                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.inventory') || 'Envanter'} *
                    </label>
                    <FormInput
                      type="number"
                      min="0"
                      step="1"
                      value={formData.inventory}
                      onChange={(e) => setFormData({ ...formData, inventory: e.target.value.replace(/\D/g, '') })}
                      placeholder="0"
                    />
                  </div>
                )}
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={handleCloseTicketModal}
              disabled={saving}
            >
              {getTranslation('common.cancel') || 'İptal'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveTicket}
              disabled={saving}
            >
              {saving
                ? (getTranslation('common.saving') || 'Kaydediliyor...')
                : (getTranslation('common.save') || 'Kaydet')
              }
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUnsavedModal}
        onClose={handleUnsavedModalClose}
        title={getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage') || 'Kaydedilmemiş değişiklikleriniz var. Devam etmek istediğinizden emin misiniz?'}
        confirmText={getTranslation('organizerPanel.saveAndContinue') || 'Kaydet ve Devam Et'}
        cancelText={getTranslation('organizerPanel.discardChanges') || 'Vazgeç'}
        onConfirm={handleUnsavedModalSave}
        onCancel={handleUnsavedModalDiscard}
        confirmVariant="primary"
      />

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTicket(null);
        }}
        title={getTranslation('organizerPanel.deleteTicket') || 'Bilet Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteTicketConfirm') || 'Bu bileti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
        confirmText={getTranslation('common.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleConfirmDelete}
        confirmVariant="primary"
        isDanger={true}
      />

      <div className={styles.header}>
        <h2 className={styles.pageTitle}>
          {getTranslation('organizerPanel.eventTickets') || 'Biletler'}
        </h2>
        <div className={styles.headerActions}>
          {!isRsvpEvent && (
            <Button
              variant="primary"
              size="medium"
              onClick={handleAddTicket}
              className={styles.addButton}
            >
              {getTranslation('organizerPanel.addTicket') || 'Bilet Ekle'}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {isRsvpEvent ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              {getTranslation('organizerPanel.rsvpEventTicketsManaged') || 'Bu etkinlik LCV modunda olusturuldugu icin ozel bilet ekleyemezsiniz.'}
            </p>
          </div>
        ) : normalTickets.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              {getTranslation('organizerPanel.noTickets') || 'Henüz bilet eklenmemiş'}
            </p>
          </div>
        ) : (
          <div className={styles.ticketsList}>
            {normalTickets.map((ticket) => {
              const price = ticket.price || 0;
              const currency = ticket.currency || 'TRY';
              const isFree = price === 0;
              const hasInventory = ticket.trackInventory === true || ticket.trackInventory === "1";
              const inventory = ticket.inventory || 0;
              const soldInventory = ticket.soldInventory || 0;
              const remainingInventory = inventory - soldInventory;

              return (
                <div key={ticket.ticketID} className={styles.ticketCard}>
                  <div className={styles.ticketActions}>
                    <button
                      onClick={() => handleEditTicket(ticket)}
                      className={styles.actionIconButton}
                      aria-label={getTranslation('common.edit') || 'Düzenle'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteTicket(ticket)}
                      className={styles.deleteIconButton}
                      aria-label={getTranslation('common.delete') || 'Sil'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <div className={styles.ticketInfo}>
                    <h3 className={styles.ticketTitle}>
                      {ticket.title}
                    </h3>
                    {ticket.description && (() => {
                      const isExpanded = expandedDescriptions.has(ticket.ticketID);
                      const description = ticket.description || '';
                      const shouldTruncate = description.length > 150;
                      const displayText = shouldTruncate && !isExpanded 
                        ? description.substring(0, 150) + '...'
                        : description;
                      
                      return (
                        <div className={styles.descriptionWrapper}>
                          <p className={styles.ticketDescription}>{displayText}</p>
                          {shouldTruncate && (
                            <button
                              className={styles.showMoreButton}
                              onClick={() => {
                                setExpandedDescriptions(prev => {
                                  const newSet = new Set(prev);
                                  if (isExpanded) {
                                    newSet.delete(ticket.ticketID);
                                  } else {
                                    newSet.add(ticket.ticketID);
                                  }
                                  return newSet;
                                });
                              }}
                            >
                              {isExpanded 
                                ? (getTranslation('common.showLess') || 'Daha Az Göster')
                                : (getTranslation('common.showMore') || 'Daha Fazla Göster')
                              }
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    <div className={styles.ticketDetails}>
                          <div className={styles.ticketDetail}>
                            <span className={styles.ticketDetailLabel}>
                              {getTranslation('organizerPanel.ticketType') || 'Bilet Tipi'}:
                            </span>
                            <span className={styles.ticketDetailValue}>
                              {ticket.type || (getTranslation('organizerPanel.ticketTypeNormal') || 'Normal')}
                            </span>
                          </div>
                          <div className={styles.ticketDetail}>
                            <span className={styles.ticketDetailLabel}>
                              {getTranslation('organizerPanel.price') || 'Fiyat'}:
                            </span>
                            <span className={styles.ticketDetailValue}>
                              {isFree
                                ? (getTranslation('organizerPanel.free') || 'Ücretsiz')
                                : `${price} ${currency}`
                              }
                            </span>
                          </div>
                      {hasInventory && (
                        <div className={styles.ticketDetail}>
                          <span className={styles.ticketDetailLabel}>
                            {getTranslation('organizerPanel.inventory') || 'Envanter'}:
                          </span>
                          <span className={styles.ticketDetailValue}>
                            {remainingInventory} / {inventory}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default EventTicketsPage;
